/**
 * Card di una risposta in bozza nel thread (Task 46), solo vista operatore.
 *
 * Una bozza nasce quando un componente del canale senza «Può rispondere
 * all'associato» risponde: il richiedente non riceve nulla finche il
 * responsabile del canale (o un admin) non la approva.
 *  - in attesa: card ambra tratteggiata nel punto cronologico in cui e stata
 *    scritta; l'autore la corregge o la ritira, chi approva la invia (anche
 *    correggendo il testo) o la rifiuta con un motivo;
 *  - rifiutata: riga compatta col motivo, mostrata solo all'autore;
 *  - approvata: non compare (c'e il messaggio pubblico «approvata da …»).
 *
 * Nessuna chiamata qui: le azioni sono del contenitore, che avvisa e ricarica.
 * Ogni callback deve RIGETTARE in caso di errore: la card resta com'era.
 * Stili in linea con i token `--a2-*` (support-v3.css non si tocca): stessi
 * nelle due vesti, dark mode compresa.
 */
import React, { useState } from 'react';
import { Check, FilePen, Paperclip, Pencil, Trash2, X, XCircle } from 'lucide-react';
import ConfirmDialog from '../../ConfirmDialog';
import { formatDate, STATUS_LABELS } from '../constants';
import type { Attachment } from '../../../services/supportApi';
import type { S3ThreadItem } from './threadModel';
import { Linkified } from '../linkify';

export interface DraftActions {
  /** Puo approvare/rifiutare le bozze di questo ticket (`viewerCanApprove`). */
  canApprove: boolean;
  /**
   * `viewerCanReply` del ticket: chi non puo rispondere direttamente non approva
   * le proprie bozze (backend 403). Assente = true.
   */
  canReply?: boolean;
  busy?: boolean;
  /** `body` presente = approvazione con il testo corretto da chi approva. */
  onApprove: (draftId: string, body?: string) => Promise<void>;
  onReject: (draftId: string, note: string) => Promise<void>;
  onEdit: (draftId: string, body: string) => Promise<void>;
  onDelete: (draftId: string) => Promise<void>;
}

interface DraftReplyCardProps extends DraftActions {
  item: S3ThreadItem;
  /** Ticket chiuso: approvare e correggere non si puo piu (409); rifiutare e ritirare si. */
  closed?: boolean;
  onOpenAttachment: (a: Attachment) => void;
}

/** Lunghezza massima del motivo del rifiuto (backend: 1000). */
export const REJECT_NOTE_MAX = 1000;

const CARD: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
  padding: '10px 12px',
  borderRadius: 12,
  border: '1.5px dashed var(--a2-warning)',
  background: 'var(--a2-warn-bg)',
};
const HEAD: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 6,
  font: "600 11.5px 'DM Sans', sans-serif",
  color: 'var(--a2-warn-fg)',
};
const BODY: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  font: "400 12.5px/1.5 'DM Sans', sans-serif",
  color: 'var(--a2-tx)',
};
const SUB: React.CSSProperties = { font: "500 11px 'DM Sans', sans-serif", color: 'var(--a2-mut)' };
const ACTIONS: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end' };
const TA: React.CSSProperties = { width: '100%', minHeight: 110, resize: 'vertical' };

type Mode = 'view' | 'edit' | 'approve';

const DraftReplyCard: React.FC<DraftReplyCardProps> = ({
  item,
  closed = false,
  canApprove,
  canReply = true,
  busy = false,
  onApprove,
  onReject,
  onEdit,
  onDelete,
  onOpenAttachment,
}) => {
  const d = item.draft!;
  const [mode, setMode] = useState<Mode>('view');
  const [text, setText] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const disabled = busy || saving;

  const run = async (fn: () => Promise<void>, after?: () => void) => {
    setSaving(true);
    try {
      await fn();
      after?.();
    } catch {
      // Avviso e ricarica li fa il contenitore: la card resta com'era.
    } finally {
      setSaving(false);
    }
  };

  // ── Rifiutata: riga compatta per l'autore ──
  if (d.status === 'rejected') {
    return (
      <div
        role="article"
        aria-label={`Bozza rifiutata di ${item.authorName}`}
        style={{ ...CARD, border: '1px dashed var(--a2-bd2)', background: 'transparent', gap: 4 }}
      >
        <span style={{ ...HEAD, color: 'var(--a2-mut)' }}>
          <XCircle size={12} aria-hidden="true" />
          Bozza rifiutata{d.reviewedByName ? ` da ${d.reviewedByName}` : ''}
          {d.reviewedAt && <span className="s3-meta">· {formatDate(d.reviewedAt)}</span>}
        </span>
        {d.reviewNote && <span style={SUB}>Motivo: {d.reviewNote}</span>}
        <details>
          <summary className="s3-meta" style={{ cursor: 'pointer' }}>
            Testo della bozza
          </summary>
          <div style={{ ...BODY, marginTop: 4, color: 'var(--a2-mut)' }}><Linkified text={item.body} /></div>
        </details>
      </div>
    );
  }

  const editing = mode !== 'view';
  const showReview = canApprove && !(d.isAuthor && !canReply);
  const closedTitle = closed ? 'Ticket chiuso: la bozza non si può più inviare né correggere' : undefined;

  return (
    <div role="article" aria-label={`Bozza in attesa di approvazione di ${item.authorName}`} style={CARD}>
      <span style={HEAD}>
        <FilePen size={12} aria-hidden="true" />
        <span>Bozza in attesa di approvazione</span>
        <span aria-hidden="true">·</span>
        <span>{item.authorName}</span>
        <span className="s3-meta">· {formatDate(item.sentAt)}</span>
        {d.updatedAt && d.updatedAt !== item.sentAt && (
          <span className="s3-meta">· corretta {formatDate(d.updatedAt)}</span>
        )}
      </span>
      <span style={SUB}>
        Non ancora inviata al richiedente
        {d.thenStatus ? ` · all'approvazione il ticket passa a «${STATUS_LABELS[d.thenStatus] || d.thenStatus}»` : ''}
      </span>

      {editing ? (
        <textarea
          className="s3-ta"
          style={TA}
          aria-label={mode === 'edit' ? 'Testo della bozza' : 'Testo da approvare'}
          value={text}
          disabled={disabled}
          autoFocus
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              setMode('view');
            }
          }}
        />
      ) : (
        <div style={BODY}><Linkified text={item.body} /></div>
      )}

      {item.attachments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {item.attachments.map((a, i) => (
            <button key={`${a.url}-${i}`} type="button" className="s3-attach" onClick={() => onOpenAttachment(a)}>
              <Paperclip size={11} aria-hidden="true" />
              <span className="s3-ellipsis">{a.filename}</span>
            </button>
          ))}
        </div>
      )}

      {editing ? (
        <div style={ACTIONS}>
          <button type="button" className="s3-pill s3-pill--sm" disabled={disabled} onClick={() => setMode('view')}>
            Annulla
          </button>
          {mode === 'edit' ? (
            <button
              type="button"
              className="s3-pill s3-pill--sm s3-pill--primary"
              disabled={disabled || !text.trim()}
              onClick={() => {
                const body = text.trim();
                if (body === item.body) {
                  setMode('view');
                  return;
                }
                void run(() => onEdit(d.id, body), () => setMode('view'));
              }}
            >
              Salva
            </button>
          ) : (
            <button
              type="button"
              className="s3-pill s3-pill--sm s3-pill--primary"
              disabled={disabled || !text.trim() || closed}
              onClick={() => {
                const body = text.trim();
                void run(() => onApprove(d.id, body === item.body ? undefined : body), () => setMode('view'));
              }}
            >
              <Check size={12} aria-hidden="true" /> Approva con modifiche
            </button>
          )}
        </div>
      ) : (
        <div style={ACTIONS}>
          {d.isAuthor && (
            <>
              <button
                type="button"
                className="s3-pill s3-pill--sm"
                disabled={disabled || closed}
                title={closedTitle}
                onClick={() => {
                  setText(item.body);
                  setMode('edit');
                }}
              >
                <Pencil size={11} aria-hidden="true" /> Modifica
              </button>
              <button
                type="button"
                className="s3-pill s3-pill--sm s3-pill--danger"
                disabled={disabled}
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 size={11} aria-hidden="true" /> Ritira
              </button>
            </>
          )}
          {showReview && (
            <>
              <button
                type="button"
                className="s3-pill s3-pill--sm s3-pill--danger"
                disabled={disabled}
                onClick={() => {
                  setRejectNote('');
                  setRejectOpen(true);
                }}
              >
                <X size={11} aria-hidden="true" /> Rifiuta
              </button>
              <button
                type="button"
                className="s3-pill s3-pill--sm"
                disabled={disabled || closed}
                title={closedTitle}
                onClick={() => {
                  setText(item.body);
                  setMode('approve');
                }}
              >
                <Pencil size={11} aria-hidden="true" /> Correggi e approva
              </button>
              <button
                type="button"
                className="s3-pill s3-pill--sm s3-pill--primary"
                disabled={disabled || closed}
                title={closedTitle}
                onClick={() => void run(() => onApprove(d.id))}
              >
                <Check size={12} aria-hidden="true" /> Approva e invia
              </button>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onConfirm={() => void run(() => onReject(d.id, rejectNote.trim()))}
        title="Rifiuta bozza"
        message={`La bozza di ${item.authorName} non viene inviata al richiedente. L'autore riceve il motivo e può scrivere una nuova risposta.`}
        confirmText="Rifiuta"
        type="danger"
      >
        <label style={{ display: 'block', marginTop: 10 }}>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Motivo (facoltativo, consigliato)</span>
          <textarea
            className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-800 dark:text-gray-100 px-2 py-1"
            rows={3}
            maxLength={REJECT_NOTE_MAX}
            placeholder="Consigliato: spiega all'autore cosa correggere"
            value={rejectNote}
            aria-label="Motivo del rifiuto"
            onChange={(e) => setRejectNote(e.target.value)}
          />
        </label>
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void run(() => onDelete(d.id))}
        title="Ritira bozza"
        message="La bozza viene eliminata e non arriverà al richiedente. Potrai scrivere una nuova risposta."
        confirmText="Ritira"
        type="danger"
      />
    </div>
  );
};

export default DraftReplyCard;
